import React, { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { useField } from 'formik'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

import { DateRange, DayPicker, OnSelectHandler } from 'react-day-picker'
import 'react-day-picker/style.css'
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material'
import theme from '../../../styles/theme'
import mapEnumForSelect from '../../../utils/mapEnumForSelect'
import { computeTimeframeValues, DateFormat, getMatchingTimeframe, Timeframe } from './StatsDateRange'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { GraphGranularity } from '../../../__generated__/graphql'
import { getAvailableGranularities } from './AvailableGranularities'
import { ToolbarFilterField } from '../../toolbar/ToolbarFilterRow'

const DisplayedDateFormat = 'DD/MM/YYYY'
const PopoverWoGranularityWidth = '860px'
const PopoverWGranularityWidth = '1000px'
const contentMaxHeight = '350px'

export const StatsDateFilterPopup = ({ withGranularity }: { withGranularity: boolean }): JSX.Element => {
  const t = useAppTranslations()

  const [, { value: startDateString }, { setValue: setStartDate }] = useField({ name: 'startDate' })
  const [, { value: endDateString }, { setValue: setEndDate }] = useField({ name: 'endDate' })
  const [, { value: granularity }, { setValue: setGranularity }] = useField({ name: 'granularity' })
  const [month, setMonth] = useState(dayjs(endDateString).subtract(1, 'month').toDate())

  const granularityList = useMemo(() => {
    const startDate = dayjs(startDateString)
    const endDate = dayjs(endDateString)

    return getAvailableGranularities(startDate, endDate)
  }, [startDateString, endDateString])

  useEffect(() => {
    const lowerGranularity = granularityList.find(({ disabled }) => !disabled)

    setGranularity(lowerGranularity?.granularity ?? GraphGranularity.Year)
  }, [granularityList])

  const onDatesChange: OnSelectHandler<DateRange | undefined> = (change): void => {
    if (change !== undefined) {
      setStartDate(dayjs(change.from).format(DateFormat))
      setEndDate(dayjs(change.to).format(DateFormat))
    }
  }

  const setTimeframe = (timeframe: Timeframe): void => {
    const newStartDate = computeTimeframeValues()[timeframe].startDate
    const newEndDate = computeTimeframeValues()[timeframe].endDate

    setStartDate(newStartDate)
    setEndDate(newEndDate)
    setMonth(dayjs(newEndDate).subtract(1, 'month').toDate())
  }

  const selectedTimeframe = useMemo(
    () => getMatchingTimeframe(startDateString, endDateString),
    [startDateString, endDateString]
  )

  const timeframeList = useMemo(
    () =>
      mapEnumForSelect(Timeframe)
        .filter((type) => type.value !== Timeframe.CUSTOM)
        .map((type) => (
          <ListItemButton
            key={type.value}
            selected={selectedTimeframe === type.value}
            onClick={() => setTimeframe(type.value as Timeframe)}
          >
            <ListItemText primary={t(`stats.filter.timeframeValues.${type.label}`)} />
          </ListItemButton>
        )),
    [selectedTimeframe, t]
  )

  return (
    <>
      <Grid
        container
        spacing={theme.spacing(1)}
        sx={{
          margin: theme.spacing(2),
          width: withGranularity ? PopoverWGranularityWidth : PopoverWoGranularityWidth,
        }}
      >
        <Grid
          sx={{
            width: 150,
          }}
        >
          <List
            dense={true}
            aria-label="timeframe-list"
            sx={{
              overflow: 'auto',
              maxHeight: contentMaxHeight,
              padding: 0,
            }}
          >
            {timeframeList}
          </List>
        </Grid>

        <Grid
          sx={{
            flex: 1,
            maxHeight: contentMaxHeight,
            overflow: 'hidden',
          }}
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .rdp-root {
                  --rdp-today-color: ${theme.palette.secondary.dark};
                  --rdp-accent-color: ${theme.palette.primary.main};
                  --rdp-weekday-color: ${theme.palette.text.secondary};
                  margin: auto;
                }
                .rdp-month_caption {
                  margin-bottom: ${theme.spacing(2)};
                }
                .rdp-dropdown_root {
                  cursor: pointer;
                }
                .rdp-weekdays {
                  color: ${theme.palette.text.secondary};
                }
                .rdp-selected {
                  font-size: ${theme.typography.body2.fontSize};
                }
                .rdp-day {
                  font-size: ${theme.typography.body2.fontSize};
                }
                .rdp-month_caption {
                  font-weight: normal;
                  font-size: ${theme.typography.body1.fontSize};
                }
                .rdp-chevron {
                  fill: ${theme.palette.grey[500]};
                }
              `,
            }}
          />
          <DayPicker
            ISOWeek
            mode="range"
            captionLayout="label"
            navLayout="around"
            selected={{
              from: new Date(startDateString),
              to: new Date(endDateString),
            }}
            onSelect={onDatesChange}
            month={month}
            onMonthChange={setMonth}
            numberOfMonths={2}
            reverseMonths={false}
            disabled={{ before: dayjs().subtract(3, 'year').toDate(), after: new Date() }}
            required
          />
        </Grid>

        {withGranularity && (
          <Grid
            sx={{
              width: 150,
            }}
          >
            <FormControl>
              <RadioGroup
                aria-labelledby="granularity-group-label"
                value={granularity}
                name="granularity-group"
                onChange={(event) => setGranularity(event.target.value as GraphGranularity)}
              >
                {granularityList.map(({ disabled, granularity }, i) => (
                  <FormControlLabel
                    key={i}
                    value={granularity}
                    disabled={disabled}
                    control={<Radio />}
                    label={t(`stats.granularity.${granularity}`)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export const StatsDateFilter = ({ withGranularity }: { withGranularity: boolean }): JSX.Element => {
  const t = useAppTranslations()
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  const [{ value: startDate }] = useField({ name: 'startDate' })
  const [{ value: endDate }] = useField({ name: 'endDate' })
  const [{ value: granularity }] = useField({ name: 'granularity' })

  const isPopoverOpen = Boolean(anchorEl)
  const id = isPopoverOpen ? 'data-range-popover' : undefined

  const selectedTimeframe = useMemo(() => getMatchingTimeframe(startDate, endDate), [startDate, endDate])

  const dateRangeLabel = useMemo(
    () => `${dayjs(startDate).format(DisplayedDateFormat)} -> ${dayjs(endDate).format(DisplayedDateFormat)}`,
    [startDate, endDate]
  )

  const isCustomTimeframe = selectedTimeframe === Timeframe.CUSTOM

  const inputValue = useMemo(() => {
    if (!isCustomTimeframe) return t(`stats.filter.timeframeValues.${selectedTimeframe}`)

    if (withGranularity) return `${dateRangeLabel} ${t(`stats.granularity.${granularity}`)}`

    return dateRangeLabel
  }, [dateRangeLabel, granularity, isCustomTimeframe, selectedTimeframe, t, withGranularity])

  const presetDatesCaption = useMemo(() => {
    if (withGranularity) return `${dateRangeLabel} · ${t(`stats.granularity.${granularity}`)}`

    return dateRangeLabel
  }, [dateRangeLabel, granularity, t, withGranularity])

  return (
    <>
      <ToolbarFilterField minWidth={350}>
        <TextField
          value={inputValue}
          variant="outlined"
          margin="none"
          label={t('stats.filter.datesRange')}
          data-testid="DateFieldTestId"
          size="small"
          fullWidth
          aria-describedby={id}
          onClick={(event) => setAnchorEl(event.currentTarget as any)}
          slotProps={{
            input: {
              readOnly: true,
              sx: { cursor: 'pointer' },
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0.5, maxHeight: 'none' }}>
                  {!isCustomTimeframe && (
                    <Box
                      component="span"
                      sx={{
                        typography: 'caption',
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {presetDatesCaption}
                    </Box>
                  )}
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </ToolbarFilterField>
      <Popover
        id={id}
        open={isPopoverOpen}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {isPopoverOpen && <StatsDateFilterPopup withGranularity={withGranularity} />}
      </Popover>
    </>
  )
}

export default StatsDateFilter
